// src/hooks/comments/index.ts
import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/auth";
import { Comment } from "@/types/social";
import { fetchComments, fetchReplies } from "@/services/comments";
import { useCommentVotes } from "./useCommentVotes";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  ContentType,
  CommentSortOption,
  CommentFilterOption,
} from "@/services/comments/types";

interface UseCommentsProps {
  contentId: string;
  contentType?: ContentType;
  limit?: number;
  sortBy?: CommentSortOption;
  filterBy?: CommentFilterOption;
  parentId?: string | null;
}

export const useComments = ({
  contentId,
  contentType = "card",
  limit = 10,
  sortBy = "newest",
  filterBy = "all",
  parentId = null,
}: UseCommentsProps) => {
  const { user } = useAuth();
  const [comments, setComments] = useState<Comment[]>([]);
  const [commentCount, setCommentCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const userId = user?.id || null;

  // Comment replies
  const [replies, setReplies] = useState<Record<string, Comment[]>>({});
  const [repliesVisible, setRepliesVisible] = useState<Record<string, boolean>>(
    {}
  );
  const [repliesLoading, setRepliesLoading] = useState<Record<string, boolean>>(
    {}
  );
  const [replyCounts, setReplyCounts] = useState<Record<string, number>>({});
  const [replyForm, setReplyForm] = useState<string | null>(null);

  // Vote counters
  const [upvoteCounts, setUpvoteCounts] = useState<Record<string, number>>({});
  const [downvoteCounts, setDownvoteCounts] = useState<Record<string, number>>(
    {}
  );

  // Get vote data and handlers
  const { userVotes, voteCounts, isVoting, handleVote, maxVotes } =
    useCommentVotes({
      comments,
      userId,
      anonymousId: null,
      contentType,
    });

  // Fetch comments
  const fetchCommentsData = useCallback(async () => {
    if (!contentId) return;

    setIsLoading(true);
    try {
      const { data, error } = await fetchComments({
        contentId,
        contentType,
        limit,
        sortBy,
        parentId,
      });

      if (error) throw error;

      if (data) {
        setComments(data as Comment[]);
        setCommentCount(data.length);

        const upVotes: Record<string, number> = {};
        const downVotes: Record<string, number> = {};

        data.forEach((comment) => {
          upVotes[comment.id] = comment.upvotes || 0;
          downVotes[comment.id] = comment.downvotes || 0;
        });

        setUpvoteCounts(upVotes);
        setDownvoteCounts(downVotes);
      }

      const replyCounts: Record<string, number> = {};

      if (data && data.length > 0) {
        for (const comment of data) {
          const { count, error } = await supabase
            .from(contentType === "poll" ? "poll_comments" : "card_comments")
            .select("id", { count: "exact", head: true })
            .eq("parent_id", comment.id)
            .is("deleted", false);

          if (!error && count !== null) {
            replyCounts[comment.id] = count;
          }
        }
      }

      setReplyCounts(replyCounts);
    } catch (error) {
      console.error("Error fetching comments:", error);
    } finally {
      setIsLoading(false);
    }
  }, [contentId, contentType, limit, sortBy, parentId]);

  useEffect(() => {
    fetchCommentsData();
  }, [fetchCommentsData]);

  const fetchRepliesData = useCallback(
    async (commentId: string) => {
      if (!commentId) return;

      setRepliesLoading((prev) => ({ ...prev, [commentId]: true }));

      try {
        const { data, error } = await fetchReplies({
          parentId: commentId,
          contentType,
          contentId,
        });

        if (error) throw error;

        if (data) {
          setReplies((prev) => ({
            ...prev,
            [commentId]: data as Comment[],
          }));
        }
      } catch (error) {
        console.error("Error fetching comment replies:", error);
      } finally {
        setRepliesLoading((prev) => ({ ...prev, [commentId]: false }));
      }
    },
    [contentId, contentType]
  );

  const handleSubmitComment = async (
    content: string,
    parentCommentId: string | null = null
  ) => {
    if (!content.trim()) return;
    if (!userId) {
      toast.error("You need to be signed in to comment");
      return;
    }

    setIsSubmitting(true);

    try {
      const { postComment } = await import("@/services/comments");
      const { data, error } = await postComment({
        contentId,
        content,
        userId,
        anonymousId: null,
        contentType,
        parentId: parentCommentId || null,
      });

      if (error) throw error;

      if (data) {
        if (!parentCommentId) {
          setComments((prev) => [data as Comment, ...prev]);
          setCommentCount((prev) => prev + 1);
        } else {
          setReplyCounts((prev) => ({
            ...prev,
            [parentCommentId]: (prev[parentCommentId] || 0) + 1,
          }));

          if (repliesVisible[parentCommentId]) {
            setReplies((prev) => {
              const existing = prev[parentCommentId] || [];
              return {
                ...prev,
                [parentCommentId]: [...existing, data as Comment],
              };
            });
          }

          setReplyForm(null);
        }
      }

      toast.success("Comment posted!");
    } catch (error) {
      console.error("Error posting comment:", error);
      toast.error("Failed to post comment");
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleReplies = useCallback(
    (commentId: string) => {
      if (!repliesVisible[commentId]) {
        fetchRepliesData(commentId);
      }

      setRepliesVisible((prev) => ({
        ...prev,
        [commentId]: !prev[commentId],
      }));
    },
    [repliesVisible, fetchRepliesData]
  );

  const toggleReplyForm = (commentId: string | null) => {
    if (!userId && commentId !== null) {
      toast.error("You need to be signed in to reply");
      return;
    }

    setReplyForm(replyForm === commentId ? null : commentId);
  };

  return {
    comments,
    commentCount,
    isLoading,
    isSubmitting,
    handleSubmitComment,
    userVotes,
    voteCounts,
    isVoting,
    handleVote,
    maxVotes,
    isAuthenticated: !!userId,
    replies,
    repliesVisible,
    repliesLoading,
    toggleReplies,
    replyForm,
    toggleReplyForm,
    replyCounts,
    upvoteCounts,
    downvoteCounts,
  };
};
